import PropTypes from 'prop-types';
import useLabels from 'infrastructure/js/hooks/useLabels';
import NameValuePairs from '../../../Common/NameValuePairs/nameValuePairs';
import TextAreaField from 'infrastructure/js/components/controls/TextAreaField/textAreaField';
import InputSection from 'infrastructure/js/components/Dialog/InputSection/inputSection';
import {enumTypes, getEnumLabel, getEnumValue} from '../../../../utils/enumHelper';
import TextField from 'infrastructure/js/components/controls/TextField/textField';
import CocRangeBar from '../../CoCRangeBar/cocRangeBar';
import Normalize from 'infrastructure/js/components/controls/controlsNormalizations';
import CoCAttachments from '../CoCAttachments/cocAttachments';
import CoCResultIndicator from '../../CoCResultIndicator/cocResultIndicator';
import {getCoCResultOptions, ResultStatus} from '../../utils/cocHelper';
import {QPL_MAX_DECIMAL, QPL_MIN_DECIMAL} from '../../../Administration/MatSettingsPage/utils/qplHelper';
import RadioButton from 'infrastructure/js/components/controls/RadioButton/radioButton';

import './cocSectionListItem.scss';

const isQualityTest = testType => testType === getEnumValue(enumTypes.TEST_TYPE)('QUALITY');

const isEvenColumn = columnOrder => columnOrder % 2 === 0;

const getValidNumericValue = (item, reduxData) => {
  const raw = reduxData.rawMaterials?.[`id-${item.id}`];
  if (raw === null || raw === undefined || raw === '' || isNaN(raw)) return null;
  return +raw;
};

const getFieldValueStatus = (item, cocData = {}, reduxData = {}) => {
  if (cocData?.passCriteria && !isEvenColumn(item.columnOrder)) return '';

  const value = getValidNumericValue(item, reduxData);
  if (value === null) return '';

  const {minimumValue, maximumValue} = cocData;
  const min = minimumValue ?? QPL_MIN_DECIMAL;
  const max = maximumValue ?? QPL_MAX_DECIMAL;

  return (value >= min && value <= max) ? 'pass' : 'fail';
};

const getResultsStatus = (results, minValue, maxValue) => {
  if (!Array.isArray(results) || results.length === 0) {
    return {
      status: '',
      minOut: null,
      maxOut: null,
    };
  }

  const min = minValue ?? QPL_MIN_DECIMAL;
  const max = maxValue ?? QPL_MAX_DECIMAL;

  let hasMissing = false;
  let hasValidNumbers = false;
  let minOut = null;
  let maxOut = null;

  for (const item of results) {
    if (item === null || item === undefined || item === '') {
      hasMissing = true;
      continue;
    }

    const num = Number(item);
    if (isNaN(num)) {
      hasMissing = true;
      continue;
    }

    hasValidNumbers = true;

    if (num < min) {
      minOut = minOut === null ? num : Math.min(minOut, num);
      continue;
    }
    if (num > max) {
      maxOut = maxOut === null ? num : Math.max(maxOut, num);
    }
  }

  const hasOutOfRange = minOut !== null || maxOut !== null;

  if (hasOutOfRange) {
    return {
      status: hasMissing ? ResultStatus.PARTIAL_FAIL : ResultStatus.FAIL,
      minOut,
      maxOut,
    };
  }

  return {
    status: !hasValidNumbers ? '' : (hasMissing ? ResultStatus.PARTIAL_PASS : ResultStatus.PASS),
    minOut: null,
    maxOut: null,
  };
};

const getQualityResultsStatus = (results) => {
  if (!Array.isArray(results) || results.length === 0) {
    return { status: '' };
  }

  let hasFailed = false;

  for (const item of results) {
    if (item === null || item === undefined || item === '') {
      return { status: '' }; //has missing results
    }

    if (item === 'FAIL') {
      hasFailed = true;
    }
  }
  return {
    status: hasFailed ? ResultStatus.FAIL : ResultStatus.PASS
  }
};

const getRangeResultStatus = (cocData = {}, reduxData = {}) => {
  const {rawMaterialsResultFieldsIds = [], minimumValue, maximumValue} = cocData;
  const rawMaterials = reduxData?.rawMaterials || {};

  const results = rawMaterialsResultFieldsIds.map(id => rawMaterials[`id-${id}`]);

  return getResultsStatus(results, minimumValue, maximumValue);

};

const getQualityResultStatus = (cocData = {}, reduxData = {}) => {
  const {rawMaterialsResultFieldsIds = []} = cocData;
  const rawMaterials = reduxData?.rawMaterials || {};

  const results = rawMaterialsResultFieldsIds.map(id => rawMaterials[`id-${id}`]);

  return getQualityResultsStatus(results);

};

const renderQualityResultIndicator = (cocData, reduxData) => {
  const rangeResult = getQualityResultStatus(cocData, reduxData);
  if ([ResultStatus.PASS, ResultStatus.FAIL].includes(rangeResult.status)) {
    return <CoCResultIndicator result={rangeResult.status}/>;
  }
  return null;
}

const renderRows = (row, rowIndex, index, name, cocData, reduxData, isViewOnly, showEmptyFieldErrors) => {
  return row.map((item, columnIndex) => {
    const itemId = `id-${item.id}`;
    const statusClass = getFieldValueStatus(item, cocData, reduxData);
    const passCriteria = cocData?.passCriteria;

    const normalizeFn = (passCriteria && !isEvenColumn(item.columnOrder)) ? undefined :
      Normalize.normalizeFloat(QPL_MIN_DECIMAL, QPL_MAX_DECIMAL, 5);

    const isResultField = cocData?.rawMaterialsResultFieldsIds?.includes(item.id);
    const emptyErrorClass = (showEmptyFieldErrors && isResultField && getValidNumericValue(item, reduxData) === null) ? 'empty-error' : '';

    return (
      <div key={`key-${index}-${rowIndex}-${columnIndex}`} className="row-material">
        <span className="raw-material-label">{item.displayName}</span>
        <TextField
          className={`raw-material-value ${statusClass} ${emptyErrorClass}`.trim()}
          id={`${name}.[rawMaterials][${item.id}]`}
          name={`${name}.[rawMaterials].${itemId}`}
          normalize={normalizeFn}
          disabled={isViewOnly}
        />
      </div>
    );
  });
}

const renderQualityRows = (row, rowIndex, index, name, cocData, reduxData, isViewOnly, showEmptyFieldErrors) => {
  return row.map((item, columnIndex) => {
    const itemId = `id-${item.id}`;

    if (!isEvenColumn(item.columnOrder)) {
      return (
        <div key={`key-${index}-${rowIndex}-${columnIndex}`} className="row-material">
          <span className="raw-material-label">{item.displayName}</span>
          <TextField
            className={'raw-material-value'}
            id={`${name}.[rawMaterials][${item.id}]`}
            name={`${name}.[rawMaterials].${itemId}`}
            disabled={isViewOnly}
          />
        </div>
      );
    }

    const isResultField = cocData?.rawMaterialsResultFieldsIds?.includes(item.id);
    const rawValue = reduxData?.rawMaterials?.[`id-${item.id}`];
    const isEmptyResultField = showEmptyFieldErrors && isResultField && (rawValue === null || rawValue === undefined || rawValue === '');

    return (
      <div key={`key-${index}-${rowIndex}-${columnIndex}`} className="row-material">
        <span className="raw-material-label">{item.displayName}</span>
        <RadioButton
          className={`short-textfield${isEmptyResultField ? ' empty-error' : ''}`}
          id={`${name}.[rawMaterials][${item.id}]`}
          name={`${name}.[rawMaterials].${itemId}`}
          labelsAndValues={getCoCResultOptions()}
          disabled={isViewOnly}
        />
      </div>
    );
  });
}

const renderRawMaterialsResults = (cocData, reduxData, index, name, isViewOnly, showEmptyFieldErrors) => {
  const rawMaterialsFields = cocData?.rawMaterialsFields ?? [];
  return rawMaterialsFields.length > 0 ? (
    <div className="raw-materials-results">
      {rawMaterialsFields.map((row, rowIndex) => (
        <div key={`row-key-${index}-${rowIndex}`} className="raw-material-row">
          {renderRows(row, rowIndex, index, name, cocData, reduxData, isViewOnly, showEmptyFieldErrors)}
        </div>
      ))}
    </div>
  ) : null;
}

const renderQualityResults = (cocData, reduxData, index, name, isViewOnly, showEmptyFieldErrors) => {
  const rawMaterialsFields = cocData?.rawMaterialsFields ?? [];
  if (rawMaterialsFields.length === 0) {
    return null;
  }
  return (
    <div className="raw-materials-results quality-results">
      {rawMaterialsFields.map((row, rowIndex) => (
        <div key={`row-key-${index}-${rowIndex}`} className="raw-material-row quality-row">
          {renderQualityRows(row, rowIndex, index, name, cocData, reduxData, isViewOnly, showEmptyFieldErrors)}
        </div>
      ))}
      {renderQualityResultIndicator(cocData, reduxData)}
    </div>
  );
}

const renderOptionalFieldsSection = (cocData) => {
  const hasOptionalFields = cocData?.optionalFields?.length > 0;
  return hasOptionalFields ? (
    <NameValuePairs className='optional-fields' items={cocData?.optionalFields}/>
  ) : null;
}

const renderComment = (labels, cocData, index, name, isViewOnly) => {
  const hasComment = cocData?.hasComment;

  return hasComment ? (
    <InputSection label={labels.get('comment')} htmlFor="comment" className="comment-section">
      <TextAreaField id={`${name}.[comment]`} name={`${name}.[comment]`} className="comment-textField" maxLength="250" disabled={isViewOnly}/>
    </InputSection>
  ) : null;
}

const createCoCSectionData = (labels, cocData) => {
  const sectionData = [ {label: labels.get('set'), value: cocData.setName} ];

  if (cocData.rawMaterialsTested) {
    sectionData.push({label: labels.get('rawMaterialsTested'), value: cocData.rawMaterialsTested});
  }
  if (cocData.passCriteria) {
    sectionData.push({label: labels.get('overallPassCriteria'), value: getEnumLabel(enumTypes.PASS_CRITERIA)(cocData.passCriteria)});
  }

  return sectionData;
}


const CoCSectionListItem = ({
                              name,
                              index: sectionIndex,
                              cocData= {},
                              reduxData = {},
                              attachmentsCounters = [],
                              attachmentsFiles = [],
                              onAttachmentUpload = null,
                              onAttachmentUploadValidation = null,
                              onAttachmentDownload = null,
                              onAttachmentRemove = null,
                              onAttachmentClick = null,
                              onAttachmentResultChange = null,
                              isViewOnly = false,
                              testType,
                              showEmptyFieldErrors = false,
                            }) => {

  const labels = useLabels('mat.forms.cocTestForm.sectionListItem.');

  const cocSectionData = createCoCSectionData(labels, cocData);

  const onAttachmentUploadHandler = (file, type) => {
    onAttachmentUpload?.(file, type, cocData.setId, sectionIndex)
  }

  const onAttachmentRemoveHandler = (file) => {
    onAttachmentRemove?.(file, sectionIndex);
  }

  const onAttachmentClickHandler = (file) => {
    onAttachmentClick?.(file, sectionIndex);
  }

  const onAttachmentResultChangeHandler = (value, file) => {
    onAttachmentResultChange?.(value, file, sectionIndex);
  }

  const renderQuantitySection = () => {
    const rangeResult = getRangeResultStatus(cocData, reduxData);
    return (
      <div className='raw-materials-section'>
        <div className='raw-materials-status-indicator'>
          <CocRangeBar label={labels.get('passCriteria')}
                       min={cocData?.minimumValue}
                       max={cocData?.maximumValue}
                       minUnit={cocData?.minUnit}
                       maxUnit={cocData?.maxUnit}
                       {...rangeResult}
          />

          {[ResultStatus.PASS, ResultStatus.FAIL].includes(rangeResult.status) &&
            <CoCResultIndicator result={rangeResult.status}/>}
        </div>

        {renderRawMaterialsResults(cocData, reduxData, sectionIndex, name, isViewOnly, showEmptyFieldErrors)}
      </div>
    )
  }

  const renderQualitySection = () => {
    return (
      <div className='raw-materials-section'>
        {renderQualityResults(cocData, reduxData, sectionIndex, name, isViewOnly, showEmptyFieldErrors)}
      </div>
    )
  }

  const renderRawMaterialsSection = () => {
    const hasRawMaterials = cocData?.rawMaterialsResultFieldsIds?.length > 0;
    if (!hasRawMaterials) {
      return null;
    }

    return isQualityTest(testType) ? renderQualitySection() : renderQuantitySection();
  }

  // Disable drag/drop events anywhere in this component so the browser doesn't
  // fall back to its native "open/save file" behavior when a file is dropped here.
  const onContainerDragOverHandler = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'none'
  }

  const onContainerDropHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
  }

  return (
    <div className='coc-section-list-item'
         onDragOver={onContainerDragOverHandler}
         onDrop={onContainerDropHandler}
    >

      <NameValuePairs items={cocSectionData}/>

      {renderOptionalFieldsSection(cocData)}

      {renderRawMaterialsSection()}

      {renderComment(labels, cocData, sectionIndex, name, isViewOnly)}

      <div className="attachments-section">
        <CoCAttachments
          index={sectionIndex} name={name}
          isViewOnly={isViewOnly}
          attachmentsCounters={attachmentsCounters}
          attachmentsFiles={attachmentsFiles}
          onAttachmentUpload={onAttachmentUploadHandler}
          onAttachmentUploadValidation={onAttachmentUploadValidation}
          onAttachmentRemove={onAttachmentRemoveHandler}
          onAttachmentClick={onAttachmentClickHandler}
          onAttachmentDownload={onAttachmentDownload}
          onAttachmentResultChange={onAttachmentResultChangeHandler}
          genericAttachmentsRequired={cocData.genericAttachmentsRequired}
          showEmptyFieldErrors={showEmptyFieldErrors}
        />
      </div>
    </div>
  )
}

CoCSectionListItem.propTypes = {
  name: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  cocData: PropTypes.object,
  reduxData: PropTypes.object,
  attachmentsCounters: PropTypes.array,
  attachmentsFiles: PropTypes.array,
  onAttachmentUpload: PropTypes.func,
  onAttachmentUploadValidation: PropTypes.func,
  onAttachmentDownload: PropTypes.func,
  onAttachmentRemove: PropTypes.func,
  onAttachmentClick: PropTypes.func,
  onAttachmentResultChange: PropTypes.func,
  isViewOnly: PropTypes.bool,
  testType: PropTypes.string,
  showEmptyFieldErrors: PropTypes.bool,
};

export default CoCSectionListItem;
